import { TableHeader } from '../../../../../shared/components/tables/paginated-table/paginated-table.component';

const cloneHeaders = (headers: TableHeader[]): TableHeader[] => headers.map((header) => ({ ...header }));

const isPartnerCategory = (type: string): boolean =>
  type === 'ACH_PARTNER' || type === 'PARTNER_CASH_ADJUSTMENTS' || type === 'PARTNER';

const COLUMN_WIDTH_CLASSES: Record<string, string> = {
  contractId: 'whitespace-nowrap min-w-[70px] w-[12%]',
  businessName: 'w-[30%]',
  recurringAmount: 'whitespace-nowrap min-w-[120px] w-[14%]',
  status: 'whitespace-nowrap min-w-[120px] w-[13%]',
  receivedOn: 'whitespace-nowrap min-w-[110px] w-[12%]',
  amountToPay: 'whitespace-nowrap min-w-[120px] w-[12%]',
  action: 'whitespace-nowrap min-w-[110px] w-[8%]',
  achArPpType: 'whitespace-nowrap min-w-[90px] w-[11%]',
  rewardType: 'whitespace-nowrap min-w-[80px] w-[15%]',
  orp: 'whitespace-nowrap min-w-[90px] w-[12%]',
  instantClaim: 'whitespace-nowrap min-w-[90px] w-[12%]',
  lastContractDate: 'whitespace-nowrap min-w-[100px] w-[13%]',
  transferToBusiness: 'whitespace-nowrap min-w-[90px] w-[12%]',
  urgentPayments: 'whitespace-nowrap min-w-[110px] w-[12%]',
  paymentCategory: 'w-[30%] min-w-[200px]',
  businesses: 'whitespace-nowrap min-w-[90px] w-[12%]',
  unpaidPayments: 'whitespace-nowrap min-w-[110px] w-[14%]',
  amount: 'whitespace-nowrap min-w-[120px] w-[14%]',
  dashboardAction: 'whitespace-nowrap min-w-[150px] w-[16%]',
  businessOverviewName: 'min-w-[180px] w-[28%]',
  businessOverviewStatus: 'whitespace-nowrap min-w-[140px] w-[15%]',
  businessOverviewUrgent: 'whitespace-nowrap min-w-[120px] w-[13%]',
  businessOverviewAmount: 'whitespace-nowrap min-w-[120px] w-[16%]',
  businessOverviewAction: 'whitespace-nowrap min-w-[260px] w-[28%]',

  // Business details (All) table - default and WON_DISPUTES
  detailsAllContractId: 'whitespace-nowrap min-w-[80px] w-[14%]',
  detailsAllRecurringAmount: 'whitespace-nowrap min-w-[120px] w-[18%]',
  detailsAllReceivedOn: 'whitespace-nowrap min-w-[110px] w-[16%]',
  detailsAllStatus: 'whitespace-nowrap min-w-[120px] w-[18%]',
  detailsAllAmountToPay: 'whitespace-nowrap min-w-[120px] w-[18%]',
  detailsAllAction: 'whitespace-nowrap min-w-[120px] w-[16%]',

  // Business details (All) table - BUSINESS_REWARDS
  rewardsType: 'whitespace-nowrap min-w-[120px] w-[30%]',
  rewardsReceivedOn: 'whitespace-nowrap min-w-[110px] w-[22%]',
  rewardsAmountToPay: 'whitespace-nowrap min-w-[120px] w-[24%]',
  rewardsAction: 'whitespace-nowrap min-w-[120px] w-[24%]',

  // Business details (All) table - ACH_PP_AR_SUB
  achPpSubContractId: 'whitespace-nowrap min-w-[80px] w-[13%]',
  achPpSubRecurringAmount: 'whitespace-nowrap min-w-[120px] w-[18%]',
  achPpSubReceivedOn: 'whitespace-nowrap min-w-[110px] w-[16%]',
  achPpSubAchArPpType: 'whitespace-nowrap min-w-[90px] w-[17%]',
  achPpSubAmountToPay: 'whitespace-nowrap min-w-[120px] w-[18%]',
  achPpSubAction: 'whitespace-nowrap min-w-[120px] w-[18%]',

  // Contract details table
  detailsContractRecurringAmount: 'whitespace-nowrap min-w-[120px] w-[26%]',
  detailsContractReceivedOn: 'whitespace-nowrap min-w-[110px] w-[22%]',
  detailsContractAmountToPay: 'whitespace-nowrap min-w-[120px] w-[26%]',
  detailsContractAction: 'whitespace-nowrap min-w-[120px] w-[26%]',
};

const applyColumnWidth = (header: TableHeader, overrideKey?: string): TableHeader => {
  const widthClass = COLUMN_WIDTH_CLASSES[overrideKey ?? header.key];
  if (!widthClass) return header;
  const existing = header.colClass?.trim();
  return { ...header, colClass: [existing, widthClass].filter(Boolean).join(' ') };
};

/** Default listing columns from admin `listingColumns` (Payment Date is not shown). */
const DEFAULT_LISTING_COLUMNS: TableHeader[] = [
  applyColumnWidth({ key: 'contractId', label: 'ID', sortable: false }),
  applyColumnWidth({ key: 'businessName', label: 'Business Name', sortable: false }),
  applyColumnWidth({ key: 'recurringAmount', label: 'Amount Deducted', sortable: false }),
  applyColumnWidth({ key: 'status', label: 'Business Status', sortable: false }),
  applyColumnWidth({ key: 'receivedOn', label: 'Received Date', sortable: false }),
  applyColumnWidth({ key: 'amountToPay', label: 'Amount to Pay', sortable: false }),
  applyColumnWidth({
    key: 'action',
    label: 'Actions',
    sortable: false,
    align: 'text-left',
  }),
];

const DEFAULT_BUSINESS_COLUMNS: TableHeader[] = [
  applyColumnWidth({ key: 'businessName', label: 'Business', sortable: false }, 'businessOverviewName'),
  applyColumnWidth({ key: 'status', label: 'Business Status', sortable: false }, 'businessOverviewStatus'),
  applyColumnWidth({ key: 'urgentPayments', label: 'Urgent Payments', sortable: false }, 'businessOverviewUrgent'),
  applyColumnWidth({ key: 'amountToPay', label: 'Amount to Pay', sortable: false }, 'businessOverviewAmount'),
  applyColumnWidth({
    key: 'action',
    label: 'Actions',
    sortable: false,
    align: 'text-left',
  }, 'businessOverviewAction'),
];

/** Default business-details columns from admin `businessDetailsAllColumns` (Payment Date is not shown). */
const DEFAULT_DETAILS_ALL_COLUMNS: TableHeader[] = [
  applyColumnWidth({ key: 'contractId', label: 'Contract ID', sortable: false }, 'detailsAllContractId'),
  applyColumnWidth({ key: 'recurringAmount', label: 'Amount Deducted', sortable: false }, 'detailsAllRecurringAmount'),
  applyColumnWidth({ key: 'receivedOn', label: 'Received Date', sortable: false }, 'detailsAllReceivedOn'),
  applyColumnWidth({ key: 'status', label: 'Contract Status', sortable: false }, 'detailsAllStatus'),
  applyColumnWidth({ key: 'amountToPay', label: 'Amount to Pay', sortable: false }, 'detailsAllAmountToPay'),
  applyColumnWidth({
    key: 'action',
    label: 'Actions',
    sortable: false,
    align: 'text-left',
  }, 'detailsAllAction'),
];

export const DEFAULT_DETAILS_CONTRACT_COLUMNS: TableHeader[] = [
  applyColumnWidth({ key: 'recurringAmount', label: 'Amount Deducted', sortable: false }, 'detailsContractRecurringAmount'),
  applyColumnWidth({ key: 'receivedOn', label: 'Received Date', sortable: false }, 'detailsContractReceivedOn'),
  applyColumnWidth({ key: 'amountToPay', label: 'Amount to Pay', sortable: false }, 'detailsContractAmountToPay'),
  applyColumnWidth({
    key: 'action',
    label: 'Action',
    sortable: false,
    align: 'text-left',
  }, 'detailsContractAction'),
];

function listingColumnsForPartner(type: string): TableHeader[] {
  return cloneHeaders(DEFAULT_LISTING_COLUMNS).map((col) => {
    if (col.key === 'businessName') {
      return { ...col, label: 'Partner Name' };
    }
    if (col.key === 'status') {
      return { ...col, label: 'Partner Status' };
    }
    return col;
  });
}

/** Listing view — aligned with admin `changeListingColumns`. */
export function changeDenefitsListingColumns(type: string): TableHeader[] {
  if (!type) {
    return cloneHeaders(DEFAULT_LISTING_COLUMNS);
  }

  if (type === 'ACH_PP_AR_SUB') {
    const columns: TableHeader[] = [
      { key: 'contractId', label: 'ID', sortable: false },
      { key: 'businessName', label: 'Business Name', sortable: false },
      { key: 'recurringAmount', label: 'Payable Amount', sortable: false },
      { key: 'status', label: 'Business Status', sortable: false },
      { key: 'receivedOn', label: 'Received Date', sortable: false },
      { key: 'achArPpType', label: 'Type', sortable: false },
      {
        key: 'action',
        label: 'Actions',
        sortable: false,
        align: 'text-left',
      },
    ];
    return columns.map((col) => applyColumnWidth(col));
  }

  if (type === 'BUSINESS_REWARDS') {
    const columns: TableHeader[] = [
      { key: 'businessName', label: 'Business Name', sortable: false },
      { key: 'rewardType', label: 'Reward Type', sortable: false },
      { key: 'recurringAmount', label: 'Payable Amount', sortable: false },
      { key: 'status', label: 'Business Status', sortable: false },
      { key: 'receivedOn', label: 'Received Date', sortable: false },
      {
        key: 'action',
        label: 'Actions',
        sortable: false,
        align: 'text-left',
      },
    ];
    return columns.map((col) => applyColumnWidth(col));
  }

  if (type === 'WON_DISPUTES') {
    const columns: TableHeader[] = [
      { key: 'contractId', label: 'ID', sortable: false },
      { key: 'businessName', label: 'Business Name', sortable: false },
      { key: 'recurringAmount', label: 'Amount Deducted', sortable: false },
      { key: 'status', label: 'Business Status', sortable: false },
      { key: 'lastContractDate', label: 'Last Contract Date', sortable: false },
      { key: 'transferToBusiness', label: 'Transfer To Business', sortable: false },
      {
        key: 'action',
        label: 'Actions',
        sortable: false,
        align: 'text-left',
      },
    ];
    return columns.map((col) => applyColumnWidth(col));
  }

  if (type === 'PROTECTED') {
    const columns: TableHeader[] = [
      { key: 'contractId', label: 'ID', sortable: false },
      { key: 'businessName', label: 'Business Name', sortable: false },
      { key: 'recurringAmount', label: 'Amount Deducted', sortable: false },
      { key: 'status', label: 'Business Status', sortable: false },
      { key: 'orp', label: 'ORP', sortable: false },
      { key: 'instantClaim', label: 'Instant Claim', sortable: false },
      { key: 'receivedOn', label: 'Received Date', sortable: false },
      { key: 'amountToPay', label: 'Amount to Pay', sortable: false },
      {
        key: 'action',
        label: 'Actions',
        sortable: false,
        align: 'text-left',
      },
    ];
    return columns.map((col) => applyColumnWidth(col));
  }

  if (isPartnerCategory(type)) {
    return listingColumnsForPartner(type);
  }

  return cloneHeaders(DEFAULT_LISTING_COLUMNS);
}

/** Dashboard overview tables — shared widths across all sections. */
export function changeDenefitsDashboardColumns(isPartnerSection: boolean): TableHeader[] {
  const entityLabel = isPartnerSection ? 'Partners' : 'Businesses';
  const columns: TableHeader[] = [
    {
      key: 'paymentCategory',
      label: 'Payments Category',
      align: 'text-left',
      sortable: false,
      noTransform: true,
      renderAsHtml: true,
      showIcon: true,
    },
    { key: 'businesses', label: entityLabel, align: 'text-left', sortable: false },
    {
      key: 'unpaidPayments',
      label: 'Unpaid Payments',
      align: 'text-left',
      sortable: false,
      noTransform: true,
      renderAsHtml: true,
    },
    {
      key: 'urgentPayments',
      label: 'Urgent Payments',
      align: 'text-left',
      sortable: false,
      noTransform: true,
      renderAsHtml: false,
    },
    {
      key: 'amount',
      label: 'Amount To Pay',
      align: 'text-left',
      sortable: false,
      noTransform: true,
      renderAsHtml: true,
    },
    { key: 'action', label: 'Action', align: 'text-left', sortable: false },
  ];

  return columns.map((col) =>
    applyColumnWidth(col, col.key === 'action' ? 'dashboardAction' : col.key)
  );
}

/** Business list — aligned with admin `changeBusinessColumns`. */
export function changeDenefitsBusinessColumns(type: string, isPartnerView: boolean): TableHeader[] {
  const columns = cloneHeaders(DEFAULT_BUSINESS_COLUMNS);

  if (isPartnerView || isPartnerCategory(type)) {
    return columns.map((col) => {
      if (col.key === 'businessName') {
        return { ...col, label: 'Business' };
      }
      if (col.key === 'status') {
        return { ...col, label: 'Partner Status' };
      }
      return col;
    });
  }

  return columns;
}

/** Business details (all payments) — aligned with admin `changebusinessDetailsCol`. */
export function changeDenefitsBusinessDetailsColumns(type: string): TableHeader[] {
  if (!type) {
    return cloneHeaders(DEFAULT_DETAILS_ALL_COLUMNS);
  }

  if (type === 'BUSINESS_REWARDS') {
    const columns: TableHeader[] = [
      { key: 'rewardType', label: 'Reward Type', sortable: false },
      { key: 'receivedOn', label: 'Received Date', sortable: false },
      { key: 'amountToPay', label: 'Amount to Pay', sortable: false },
      {
        key: 'action',
        label: 'Actions',
        sortable: false,
        align: 'text-left',
      },
    ];
    return columns.map((col) => {
      let overrideKey = col.key;
      if (col.key === 'rewardType') overrideKey = 'rewardsType';
      else if (col.key === 'receivedOn') overrideKey = 'rewardsReceivedOn';
      else if (col.key === 'amountToPay') overrideKey = 'rewardsAmountToPay';
      else if (col.key === 'action') overrideKey = 'rewardsAction';
      return applyColumnWidth(col, overrideKey);
    });
  }

  if (type === 'WON_DISPUTES') {
    const columns: TableHeader[] = [
      { key: 'contractId', label: 'ID', sortable: false },
      { key: 'recurringAmount', label: 'Amount Deducted', sortable: false },
      { key: 'receivedOn', label: 'Received Date', sortable: false },
      { key: 'status', label: 'Contract Status', sortable: false },
      { key: 'amountToPay', label: 'Amount to Pay', sortable: false },
      {
        key: 'action',
        label: 'Actions',
        sortable: false,
        align: 'text-left',
      },
    ];
    return columns.map((col) => {
      let overrideKey = col.key;
      if (col.key === 'contractId') overrideKey = 'detailsAllContractId';
      else if (col.key === 'recurringAmount') overrideKey = 'detailsAllRecurringAmount';
      else if (col.key === 'receivedOn') overrideKey = 'detailsAllReceivedOn';
      else if (col.key === 'status') overrideKey = 'detailsAllStatus';
      else if (col.key === 'amountToPay') overrideKey = 'detailsAllAmountToPay';
      else if (col.key === 'action') overrideKey = 'detailsAllAction';
      return applyColumnWidth(col, overrideKey);
    });
  }

  if (type === 'ACH_PP_AR_SUB') {
    const columns: TableHeader[] = [
      { key: 'contractId', label: 'ID', sortable: false },
      { key: 'recurringAmount', label: 'Amount Deducted', sortable: false },
      { key: 'receivedOn', label: 'Received Date', sortable: false },
      { key: 'achArPpType', label: 'Type', sortable: false },
      { key: 'amountToPay', label: 'Amount to Pay', sortable: false },
      {
        key: 'action',
        label: 'Actions',
        sortable: false,
        align: 'text-left',
      },
    ];
    return columns.map((col) => {
      let overrideKey = col.key;
      if (col.key === 'contractId') overrideKey = 'achPpSubContractId';
      else if (col.key === 'recurringAmount') overrideKey = 'achPpSubRecurringAmount';
      else if (col.key === 'receivedOn') overrideKey = 'achPpSubReceivedOn';
      else if (col.key === 'achArPpType') overrideKey = 'achPpSubAchArPpType';
      else if (col.key === 'amountToPay') overrideKey = 'achPpSubAmountToPay';
      else if (col.key === 'action') overrideKey = 'achPpSubAction';
      return applyColumnWidth(col, overrideKey);
    });
  }

  return cloneHeaders(DEFAULT_DETAILS_ALL_COLUMNS);
}
